import { inject, Injectable } from '@angular/core';

import {
  Action,
  Selector,
  State,
  StateContext,
  StateToken,
  Store
} from '@ngxs/store';
import { delay, EMPTY, switchMap, tap } from 'rxjs';

import { RemoteError } from '@app/core/errors/models/remote-error.model';
import { MyWorldHttpService } from '@app/core/services/http/my-world-http.service';
import { CustomerState } from '@app/core/store/customer/customer.state';
import { ToastService } from '@app/core/ui/components/toast';
import { CoverEnum } from '@app/features/home/store/products/products.model';
import { StudentState } from '@app/features/student/store/student/student.state';
import { DocumentTypeEnum } from '@app/shared/enums/document-type.enum';
import { LoadingStateEnum } from '@app/shared/enums/loading-state.enum';
import { catchRemoteError } from '@app/shared/rxjs/custom-operators';

import {
  FinishCheckoutAction,
  ResetLoadingAction
} from './cart-checkout.actions';
import { CartCheckoutStateModel } from './cart-checkout.model';
import {
  isBemobiShopCheckoutResponse,
  isEskolareShopCheckoutResponse,
  ShopCheckoutResponse
} from '../../models/shop-checkout-response.model';
import {
  BemobiCheckoutService,
  CheckoutOrchestratorService,
  EskolareCheckoutService
} from '../../services/flows';
import { toCheckoutExecuteParams } from '../../utils/checkout-response.mapper';
import { ToggleCartDrawerAction } from '../cart/cart-ui.actions';
import { CartItem } from '../cart/cart.model';
import { CartState } from '../cart/cart.state';

const SHOP_CHECKOUT_ERROR_MESSAGE =
  'Não foi possivel gerar link de pagamento. Tente novamente';
const CHECKOUT_FLOW_ERROR_MESSAGE =
  'Não foi possivel iniciar checkout. Tente novamente';

const CART_CHECKOUT_STATE_TOKEN = new StateToken<CartCheckoutStateModel>(
  'cart_checkout'
);
@State<CartCheckoutStateModel>({
  name: CART_CHECKOUT_STATE_TOKEN,
  defaults: {
    requestStatus: LoadingStateEnum.idle
  }
})
@Injectable()
export class CartCheckoutState {
  private readonly store = inject(Store);
  private readonly http = inject(MyWorldHttpService);
  private readonly toastService = inject(ToastService);
  private readonly checkoutOrchestrator = inject(CheckoutOrchestratorService);

  readonly cartItems = this.store.selectSignal(CartState.cartItems());
  readonly minQuantityForKit = this.store.selectSignal(
    CartState.minQuantityForKit
  );

  readonly customer = this.store.selectSignal(CustomerState.customer);
  readonly student = this.store.selectSignal(StudentState.student);
  readonly subtotal = this.store.selectSignal(CartState.subtotal());

  @Selector()
  static requestStatus(state: CartCheckoutStateModel | null): LoadingStateEnum {
    return state?.requestStatus ?? LoadingStateEnum.idle;
  }

  @Selector()
  static isLoading(state: CartCheckoutStateModel | null): boolean {
    return (
      (state?.requestStatus ?? LoadingStateEnum.idle) ===
      LoadingStateEnum.loading
    );
  }

  @Action(ResetLoadingAction)
  resetLoading({ patchState }: StateContext<CartCheckoutStateModel>) {
    patchState({ requestStatus: LoadingStateEnum.idle });
  }

  @Action(FinishCheckoutAction)
  finishCheckout(
    { patchState, dispatch }: StateContext<CartCheckoutStateModel>,
    { payload }: FinishCheckoutAction
  ) {
    patchState({ requestStatus: LoadingStateEnum.loading });

    const requestPayload = this.buildFinishCheckoutPayload(
      payload.email,
      payload.phone,
      payload.name,
      payload.document,
      payload.documentType,
      this.cartItems()
    );

    return this.http.initShopCheckout(requestPayload).pipe(
      catchRemoteError(
        (remoteError) => this.handleCheckoutError(remoteError, patchState),
        {
          defaultMessage: SHOP_CHECKOUT_ERROR_MESSAGE
        }
      ),
      switchMap((response) =>
        this.runCheckoutFlow(response, patchState, dispatch)
      )
    );
  }

  private runCheckoutFlow(
    response: ShopCheckoutResponse,
    patchState: StateContext<CartCheckoutStateModel>['patchState'],
    dispatch: StateContext<CartCheckoutStateModel>['dispatch']
  ) {
    if (!this.isShopResponseValid(response)) {
      this.toastService.danger(SHOP_CHECKOUT_ERROR_MESSAGE);
      patchState({ requestStatus: LoadingStateEnum.error });
      return EMPTY;
    }

    return this.checkoutOrchestrator
      .execute(toCheckoutExecuteParams(response))
      .pipe(
        tap(() => this.completeCheckout(patchState, dispatch)),
        catchRemoteError(
          (remoteError) => this.handleCheckoutError(remoteError, patchState),
          {
            defaultMessage: CHECKOUT_FLOW_ERROR_MESSAGE
          }
        ),
        delay(300)
      );
  }

  private completeCheckout(
    patchState: StateContext<CartCheckoutStateModel>['patchState'],
    dispatch: StateContext<CartCheckoutStateModel>['dispatch']
  ) {
    patchState({ requestStatus: LoadingStateEnum.loaded });
    dispatch(new ToggleCartDrawerAction(false));
  }

  private handleCheckoutError(
    remoteError: RemoteError,
    patchState: StateContext<CartCheckoutStateModel>['patchState']
  ) {
    this.toastService.danger(remoteError.message);
    patchState({ requestStatus: LoadingStateEnum.error });
  }

  private isShopResponseValid(response: ShopCheckoutResponse): boolean {
    if (isEskolareShopCheckoutResponse(response)) {
      return EskolareCheckoutService.isValidResponse(response);
    }

    if (isBemobiShopCheckoutResponse(response)) {
      return BemobiCheckoutService.isValidStrategy(response);
    }

    return false;
  }

  private buildFinishCheckoutPayload(
    email: string,
    phone: string,
    name: string,
    document: string,
    documentType: DocumentTypeEnum,
    items: CartItem[]
  ) {
    const { paperbackCount, hardcoverCount } = items.reduce(
      (acc, item) => {
        const quantity = item.isKit
          ? (item.kitSize || 1) * item.quantity
          : item.quantity;

        if (item.coverType === CoverEnum.Paperback) {
          acc.paperbackCount += quantity;
        } else if (item.coverType === CoverEnum.Hardback) {
          acc.hardcoverCount += quantity;
        }

        return acc;
      },
      { paperbackCount: 0, hardcoverCount: 0 }
    );

    return {
      email,
      phone,
      name,
      document,
      documentType,
      paperbackCount,
      hardcoverCount,
      studentId: this.student().id,
      totalPrice: this.subtotal(),
      bondChild: this.customer().bondChild,
      clientId: this.customer().id
    };
  }
}
